import { useState, useEffect } from 'preact/hooks';
import { styled } from 'goober';
import { useConversation } from '@elevenlabs/react';
import { useUnifiedWidget } from '../providers/UnifiedWidgetProvider';
import { useConfig } from '../providers/ConfigProvider';
import { getAgentVoiceStatus } from '../hooks/voiceMessages';
import MicIcon from '../icons/Voice';
import CloseIcon from '../icons/Close';
import WarningIcon from '../icons/Warning';

const Container = styled('div')<{ open: boolean }>`
  position: absolute;
  bottom: 0;
  width: 100%;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  flex-direction: column;
  text-align: center;
  overflow: hidden;
  visibility: ${({ open }) => open ? "visible" : "hidden"};
  opacity: ${({open}) => open ? 1 : 0};
  transform: ${({ open }) => open ? "none" : "translateY(20px) scale(0.95)"};
  transition: all 0.3s ease;
  pointer-events: ${({ open }) => open ? "auto" : "none"};
`;

const Title = styled('h3')`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
`;

const Content = styled('div')<{ isMinimized: boolean }>`
  flex: 1;
  padding: 20px;
  display: ${({ isMinimized }) => isMinimized ? 'none' : 'flex'};
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
`;

const Button = styled('button')<{ disabled?: boolean, backgroundColor?: string, color?: string }>`
  width: 100%;
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  min-width: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: ${({ disabled }) => disabled ? '0.6' : '1'};
  
  ${({ color, backgroundColor }) => {
    return `
      background: ${backgroundColor};
      color: ${color};
    `;
}}
`;

const StatusIndicator = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f4f5f6;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
`;

const StatusDot = styled('div')<{ status: 'connected' | 'connecting' | 'disconnected' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ status }) => {
    switch (status) {
      case 'connected': return '#10b981';
      case 'connecting': return '#f59e0b';
      default: return '#6b7280';
    }
  }};
  ${({ status }) => status === 'connecting' ? 'animation: pulse 2s infinite;' : ''}

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const StatusText = styled('span')<{ color: string }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ color }) => color};
`;

const ErrorMessage = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
`;

const Spinner = styled('div')<{ color: string }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${({ color }) => color};
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const FormContainer = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled('label')<{ color: string }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ color }) => color};
  margin-bottom: 4px;
  display: block;
`;

const Input = styled('input')<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ hasError }) => {
    if (hasError) return '#dc2626';
    return '#d1d5db';
  }};
  border-radius: 6px;
  font-size: 16px;
  background: #ffffff;
  box-sizing: border-box;
  color: #111827;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => hasError ? '#dc2626' : '#3b82f6'};
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const HelperText = styled('p')<{ isError?: boolean, color: string }>`
  font-size: 12px;
  margin: 0;
  color: ${({ isError, color }) => isError ? '#dc2626' : color};
  opacity: ${({ isError }) => isError ? 1 : 0.7};
`;

export function VoiceContent() {
  const { 
    open,
    mode,
    isMinimized, 
    conversationId, 
    setConversationId, 
    error, 
    setError
  } = useUnifiedWidget();
  const { config } = useConfig();
  const [isConnecting, setIsConnecting] = useState(false);
  const [elevenlabsAgentId, setElevenlabsAgentId] = useState<string | null>(null);
  const [agentStatusLoaded, setAgentStatusLoaded] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [emailSubmitted, setEmailSubmitted] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const isVoiceMode = mode === 'voice';
  const showVoice = open && isVoiceMode;

  // Load agent status when component mounts and is in voice mode
  useEffect(() => {
    const loadAgentStatus = async () => {
      if (!config.agentId || agentStatusLoaded || !isVoiceMode) {
        return;
      }

      const apiUrl = process.env.WEBSITE_DOMAIN || 'https://localhost:8014';

      try {
        console.log('🎤 VOICE WIDGET: Loading agent status for:', config.agentId);
        const status = await getAgentVoiceStatus(config.agentId, apiUrl);
        
        console.log('🎤 VOICE WIDGET: Agent status loaded:', status);
        
        if (status.success && status.has_elevenlabs_agent && status.elevenlabs_agent_id) {
          setElevenlabsAgentId(status.elevenlabs_agent_id);
          console.log('🎤 VOICE WIDGET: Set ElevenLabs Agent ID:', status.elevenlabs_agent_id);
        } else {
          setError(status.error_message || 'Agent does not have voice capabilities enabled');
        }
        
        setAgentStatusLoaded(true);
      } catch (error) {
        console.error('🎤 VOICE WIDGET: Failed to load agent status:', error);
        setError(error instanceof Error ? error.message : 'Failed to load agent configuration');
        setAgentStatusLoaded(true);
      }
    };

    loadAgentStatus();
  }, [config.agentId, agentStatusLoaded, isVoiceMode]);

  const conversation = useConversation({
    onConnect: () => {
      console.log('🟢 ElevenLabs WebSocket CONNECTED');
      console.log('   Agent ID:', config.agentId);
      console.log('   Conversation ID:', conversationId);
      console.log('   💡 Messages will be saved via ElevenLabs webhook after call ends');
      setIsConnecting(false);
      setError(null);
    },
    onDisconnect: (details) => {
      console.log('🔴 ElevenLabs WebSocket DISCONNECTED');
      console.log('   Disconnect details:', details);
      console.log('   Reason:', details?.reason || 'Unknown');
      console.log('   ✅ ElevenLabs will send full transcript via webhook');
      
      // Extract error information from the disconnect details
      const detailsAny = details as any;
      const code = detailsAny?.code || detailsAny?.context?.code;
      const message = detailsAny?.message;
      const reason = details?.reason;
      
      // Check for specific error types
      if (code === 1008 || (typeof reason === 'string' && (reason.includes('quota') || reason.includes('credit')))) {
        setError('💳 ElevenLabs credits exhausted or quota exceeded');
      } else if (code === 1002 || (message && typeof message === 'string' && message.includes('misconfigured'))) {
        setError(`💳 ElevenLabs Error: ${message || 'Agent appears to be misconfigured'}\n\n⚠️ This often indicates quota/credits exhausted despite the misleading error message.\n\nCheck your ElevenLabs dashboard for actual quota status.`);
      } else if (code === 1011) {
        setError('⚠️ ElevenLabs server error - check service status');
      } else if (code === 1006) {
        setError('⚠️ Connection closed abnormally - check network/account status');
      } else if (reason === 'error' && message) {
        setError(`Connection error: ${message}`);
      }
      
      setConversationId(null);
      setIsConnecting(false);
    },
    onError: (error) => {
      console.error('🚨 ElevenLabs ERROR:', error);
      console.error('   Error type:', typeof error);
      console.error('   Error details:', JSON.stringify(error, null, 2));
      
      let errorMessage = 'Connection error occurred';
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object') {
        const errorAny = error as any;
        if ('message' in errorAny && typeof errorAny.message === 'string') {
          errorMessage = errorAny.message;
        } else if ('error' in errorAny && typeof errorAny.error === 'string') {
          errorMessage = errorAny.error;
        } else if ('detail' in errorAny && typeof errorAny.detail === 'string') {
          errorMessage = errorAny.detail;
        }
      }
      
      if (errorMessage.toLowerCase().includes('quota') || 
          errorMessage.toLowerCase().includes('credit') ||
          errorMessage.toLowerCase().includes('limit') ||
          errorMessage.toLowerCase().includes('billing')) {
        errorMessage = '💳 ElevenLabs credits/quota issue: ' + errorMessage;
      }
      
      setError(errorMessage);
      setIsConnecting(false);
    },
    onMessage: (message) => {
      console.log('📨 Message received from ElevenLabs (will be saved via webhook):');
      console.log('   Source:', message.source);
      console.log('   Message:', message.message);
    },
    onDebug: (debugInfo) => {
      console.log('🔍 ElevenLabs Debug Info:', debugInfo);
    }
  });

  const { status, isSpeaking } = conversation;

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = (e: Event) => {
    e.preventDefault();
    setEmailError(null);

    if (!userEmail.trim()) {
      setEmailError('Please enter your email address');
      return;
    }

    if (!validateEmail(userEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setEmailSubmitted(true);
    console.log('✅ Email captured:', userEmail);
  };

  const handleStartConversation = async () => {
    if (!config.agentId) {
      setError('No agent ID provided');
      return;
    }

    if (!elevenlabsAgentId) {
      setError('ElevenLabs agent ID not loaded. Please wait or check agent configuration.');
      return;
    }

    try {
      setIsConnecting(true);
      setError(null);

      console.log('🚀 Starting ElevenLabs conversation...');
      console.log('   Public Agent ID:', config.agentId);
      console.log('   ElevenLabs Agent ID:', elevenlabsAgentId);
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Browser does not support microphone access');
      }
      
      console.log('🎤 Requesting microphone access...');
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('✅ Microphone access granted');
      console.log('   Audio tracks:', stream.getAudioTracks().length);
      console.log('   Stream active:', stream.active);
      
      console.log('🔗 Starting ElevenLabs session...');
      console.log('   Session config:', { 
        agentId: elevenlabsAgentId,
        dynamicVariables: { user_email: userEmail }
      });
      
      const convId = await conversation.startSession({
        agentId: elevenlabsAgentId,
        dynamicVariables: {
          user_email: userEmail,
        },
      } as any);
      
      setConversationId(convId);
      console.log('✅ ElevenLabs session started successfully!');
      console.log('   Conversation ID:', convId);
      console.log('   Status after start:', conversation.status);
      
    } catch (error: unknown) {
      console.error('❌ Error starting conversation:', error);
      console.error('   Error type:', typeof error);
      console.error('   Error name:', error instanceof Error ? error.name : 'Unknown');
      console.error('   Error message:', error instanceof Error ? error.message : 'Unknown');
      
      let errorMessage = 'Failed to start conversation';
      if (error instanceof Error) {
        errorMessage = error.message;
        
        if (error.message.toLowerCase().includes('quota') || 
            error.message.toLowerCase().includes('credit') ||
            error.message.toLowerCase().includes('limit') ||
            error.message.toLowerCase().includes('billing') ||
            error.message.toLowerCase().includes('insufficient')) {
          errorMessage = '💳 ElevenLabs credits/quota issue: ' + error.message;
        }
      }
      
      setError(errorMessage);
      setIsConnecting(false);
    }
  };

  const handleEndConversation = async () => {
    try {
      await conversation.endSession();
      setConversationId(null);
      setError(null);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to end conversation';
      setError(errorMessage);
    }
  };

  const isConnected = status === 'connected';

  // Cleanup: end conversation when widget is closed
  useEffect(() => {
    // If the widget was closed and we have an active conversation, end it
    if (!open && conversationId && isConnected) {
      console.log('🔴 Widget closed with active conversation - ending conversation...');
      conversation.endSession().catch((error) => {
        console.error('❌ Error ending conversation on widget close:', error);
      });
      setConversationId(null);
      setError(null);
    }
  }, [open, conversationId, isConnected]);

  return (
    <Container open={showVoice}>
      <Content isMinimized={isMinimized}>
        <Title>{config.voiceTitleText}</Title>

        {config.showEmailCollectionForm && !emailSubmitted ? (
          <FormContainer>
            <Label color={config.textColor}>
              {config.voiceSubtitleText}
            </Label>
            <form onSubmit={handleEmailSubmit} style={{ width: '100%' }}>
              <Input
                type="email"
                placeholder="you@example.com"
                value={userEmail}
                onInput={(e: any) => {
                  setUserEmail(e.target.value);
                  setEmailError(null);
                }}
                hasError={!!emailError}
                disabled={!agentStatusLoaded || !elevenlabsAgentId}
              />
              {emailError && (
                <HelperText isError>
                  {emailError}
                </HelperText>
              )}
              {!emailError && (
                <HelperText color={config.textColor}>
                  {config.voiceHelperText}
                </HelperText>
              )}
              <Button
                type="submit"
                disabled={!agentStatusLoaded || !elevenlabsAgentId}
                style={{ marginTop: '16px' }}
                backgroundColor={config.primaryColor}
                color={config.buttonTextColor}
              >
                {!agentStatusLoaded ? (
                  <>
                    <Spinner color={config.primaryColor} />
                    Loading Agent...
                  </>
                ) : !elevenlabsAgentId ? (
                  <>
                    <WarningIcon width={20} height={20} fill={config.buttonTextColor} /> Agent Not Configured
                  </>
                ) : (
                  <>
                    Continue →
                  </>
                )}
              </Button>
            </form>
          </FormContainer>
        ) : (
          <>
            {!isConnected ? (
              <Button
                onClick={handleStartConversation}
                disabled={isConnecting || !agentStatusLoaded || !elevenlabsAgentId}
                backgroundColor={config.primaryColor}
                color={config.buttonTextColor}
              >
                {isConnecting ? (
                  <>
                    <Spinner color={config.primaryColor} />
                    Connecting...
                  </>
                ) : (
                  <>
                    <MicIcon fill={config.buttonTextColor} width={22} height={22} /> {config.startVoiceConversationText}
                  </>
                )}
              </Button>
            ) : (
              <Button backgroundColor={config.primaryColor} color={config.buttonTextColor} onClick={handleEndConversation}>
                <CloseIcon fill={config.buttonTextColor} width={20} height={20} /> {config.endVoiceConversationText}
              </Button>
            )}

            <StatusIndicator>
              <StatusDot status={isConnected ? 'connected' : isConnecting ? 'connecting' : 'disconnected'} />
              <StatusText color={config.textColor}>
                {isConnected ? 'Connected' : isConnecting ? 'Connecting...' : 'Disconnected'}
              </StatusText>
            </StatusIndicator>

            {isConnected && (
              <StatusIndicator>
                <StatusDot status={isSpeaking ? 'connecting' : 'disconnected'} />
                <StatusText color={config.textColor}>
                  {isSpeaking ? 'Agent is speaking...' : 'Listening'}
                </StatusText>
              </StatusIndicator>
            )}
          </>
        )}

        {error && (
          <ErrorMessage>
            <WarningIcon width={20} height={20} fill={"#dc2626"} /> {error}
          </ErrorMessage>
        )}
      </Content>
    </Container>
  );
}

